import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bookmark, ChevronRight, CloudUpload, Compass, ScanLine, ShoppingBag, Sun, Wand2 } from "lucide-react";

import { api } from "@/lib/api";
import { BrandMark } from "@/components/cards";
import { LearnCard, ProfileSummary, RoutineSection, StyleCarousel } from "@/components/home";
import { Page } from "@/components/layout";
import { useApp } from "@/store";

const TIP_IMG = "https://static.prod-images.emergentagent.com/jobs/ec39b326-0334-4cb0-ac57-d53329ee9235/images/089cd7f26ebab144167213a54fbb7ddc8b79928b4e7dee2fc2a0ae9b82616d83.jpeg";

function greeting() {
  const h = new Date().getHours();
  if (h < 5) return "GOOD EVENING.";
  if (h < 12) return "GOOD MORNING.";
  if (h < 18) return "GOOD AFTERNOON.";
  return "GOOD EVENING.";
}

export default function Home() {
  const { user, mode, hasProfile, saved, recs, recsError, refreshRecs } = useApp();
  const [savedStyles, setSavedStyles] = useState([]);
  const [tip, setTip] = useState(null);
  const [tipError, setTipError] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!saved.length) return setSavedStyles([]);
    api(`/hairstyles?slugs=${saved.join(",")}`)
      .then((r) => setSavedStyles(saved.map((x) => r.find((y) => y.slug === x)).filter(Boolean)))
      .catch(() => setSavedStyles([]));
  }, [saved]);
  const routineStyle = savedStyles[0] ?? (hasProfile ? recs?.[0]?.style : undefined);
  const loadTip = async () => {
    try {
      setTipError(false);
      setTip((await api("/tips/daily")).tip);
    } catch {
      setTipError(true);
    }
  };
  useEffect(() => {
    loadTip();
  }, []);

  const quick = [
    { key: "scan", title: hasProfile ? "Scan Again" : "Scan Now", sub: "Face & hair estimate", icon: ScanLine, to: "/scan" },
    { key: "tryon", title: "Try On", sub: "AI hairstyle preview", icon: Wand2, to: "/try-on" },
    { key: "explore", title: "Explore", sub: "All 40 styles", icon: Compass, to: "/explore" },
    { key: "shop", title: "Shop BREEZ", sub: "Your BREEZ routine", icon: ShoppingBag, to: "/shop" },
  ];

  return (
    <div data-testid="home-screen">
      <div className="flex items-center justify-between border-b border-divider bg-off px-5 py-2 md:hidden">
        <BrandMark testId="home-wordmark" height={30} className="py-2 font-display text-[22px] tracking-[6px] text-navy" />
        {saved.length ? <SavedPill n={saved.length} /> : null}
      </div>
      <Page wide className="flex flex-col gap-6 py-6 md:gap-8 md:py-10">
        <div className="animate-fade-up flex items-end justify-between gap-4">
          <div>
            <p className="t-label">{user?.name ? `Hey ${user.name}` : mode === "guest" ? "Guest mode" : "Welcome"}</p>
            <h1 className="t-h1 mt-1 md:!text-5xl md:!leading-[52px]">
              <span data-testid="home-greeting" className="block">{greeting()}</span>
              <span className="block text-ocean">READY TO STYLE?</span>
            </h1>
          </div>
          {saved.length ? <div className="hidden md:block"><SavedPill n={saved.length} /></div> : null}
        </div>

        <div className="grid gap-6 lg:grid-cols-[1.3fr_1fr]">
          <div className="animate-fade-up flex flex-col gap-6" style={{ animationDelay: "80ms" }}>
            <ProfileSummary />
            <div className="grid grid-cols-2 gap-3">
              {quick.map((q, i) => (
                <button key={q.key} type="button" data-testid={`home-quick-${q.key}`} onClick={() => navigate(q.to)} className="focus-ring animate-fade-up min-h-[130px] rounded-[20px] border border-line bg-white p-4 text-left transition-all hover:-translate-y-0.5 hover:border-ocean hover:shadow-card active:scale-[0.98]" style={{ animationDelay: `${200 + i * 60}ms` }}>
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-tert"><q.icon className="h-[22px] w-[22px] text-navy" aria-hidden /></span>
                  <span className="t-title mt-3 block">{q.title}</span>
                  <span className="t-caption block truncate">{q.sub}</span>
                </button>
              ))}
            </div>
          </div>
          <div data-testid="daily-tip-card" className="animate-fade-up relative flex min-h-[200px] flex-col justify-end overflow-hidden rounded-[20px] bg-navy" style={{ animationDelay: "300ms" }}>
            <img src={TIP_IMG} alt="" className="absolute inset-0 h-full w-full object-cover" />
            <div className="absolute inset-0 bg-[rgba(16,39,72,0.45)]" />
            <div className="relative m-3 rounded-xl border border-glass bg-glass-dark p-4">
              <p className="mb-1.5 flex items-center gap-1.5 font-body-bold text-[11px] tracking-[1.4px] text-sky"><Sun className="h-3.5 w-3.5" aria-hidden /> DAILY STYLING TIP</p>
              {tipError ? (
                <button type="button" data-testid="daily-tip-retry" onClick={loadTip} className="focus-ring rounded text-left font-body-semi text-[15px] leading-[21px] text-white">Couldn&apos;t load today&apos;s tip. Tap to refresh.</button>
              ) : (
                <p data-testid="daily-tip-text" className="font-body-semi text-[15px] leading-[21px] text-white">{tip ?? "Loading today's tip…"}</p>
              )}
            </div>
          </div>
        </div>

        {hasProfile && recs?.length ? (
          <StyleCarousel title="Recommended for you" styles={recs.map((r) => r.style)} testPrefix="home-recs" badges={false} action="See all" onAction={() => navigate("/recommendations")} />
        ) : hasProfile && recsError ? (
          <button type="button" data-testid="home-recs-retry" onClick={refreshRecs} className="focus-ring t-caption text-left">{recsError} Tap to retry.</button>
        ) : null}

        {savedStyles.length ? <StyleCarousel title="Saved styles" styles={savedStyles} testPrefix="home-saved" action="My Style" onAction={() => navigate("/mystyle")} /> : null}
        {routineStyle ? <RoutineSection style={routineStyle} /> : null}
        <LearnCard />

        {mode === "guest" ? (
          <button type="button" data-testid="home-guest-signup" onClick={() => navigate("/auth?mode=register")} className="focus-ring flex items-center gap-3 rounded-[20px] bg-tert p-4 text-left transition-colors hover:bg-sky">
            <CloudUpload className="h-5 w-5 text-navy" aria-hidden />
            <span className="t-strong flex-1">Create a free account to sync your profile and saved styles.</span>
            <ChevronRight className="h-[18px] w-[18px] text-mute" aria-hidden />
          </button>
        ) : null}
      </Page>
    </div>
  );
}

function SavedPill({ n }) {
  return (
    <span className="flex items-center gap-1 rounded-full bg-sky px-2.5 py-1.5 font-body-semi text-xs text-navy">
      <Bookmark className="h-3 w-3 fill-navy" aria-hidden /> {n} saved
    </span>
  );
}
